// muncher.ts — the ONE shared geometric muncher draw, twin of icons.ts. The handoff draws the
// player as a cyan pac-style circle+wedge and the rival as a magenta triangle, NOT the legacy
// mascot PNGs. Every surface (splash, menu hero, board-cook, in-board sprites, results, skins
// preview, share card) routes through these so the look is one system and an art swap is a no-op.
// Renderer-only: Phaser Graphics + PALETTE; no Core / data / network / determinism touch.
import Phaser from 'phaser';
import { PALETTE } from './keys';

const rad = Phaser.Math.DegToRad;

export interface MuncherOpts {
  /** mouth half-angle in degrees (animated 26↔42 for the chomp). default 28. */
  mouthDeg?: number;
  /** draw the soft accent glow disc behind the body. default true. */
  glow?: boolean;
  /** override the body fill (equipped cosmetic tint). default PALETTE.player. */
  tint?: number;
  /** override the silhouette stroke. default PALETTE.playerStroke. */
  stroke?: number;
}

/**
 * Player muncher — an ORIGINAL flat-neon cyan chomp CREATURE: a rounded body with two stubby feet, a
 * big friendly eye, and a wide chomp mouth opening LEFT (mouthDeg is animated for the chomp). The eye
 * + feet deliberately distance it from a bare pac-style wedge (original IP, no lookalike — matches the
 * raster AutoSprite muncher). Drawn at the local origin so every surface (splash/menu/board/result/
 * share) shares one identity. `tint` recolors the body for cosmetics; eye/feet outline use `stroke`.
 */
export function drawPlayerMuncher(g: Phaser.GameObjects.Graphics, s: number, opts: MuncherOpts = {}): void {
  const fill = opts.tint ?? PALETTE.player;
  const stroke = opts.stroke ?? PALETTE.playerStroke;
  const r = s * 0.44;
  // Wide mouth opening to the LEFT (gap centered at 180°), animated 26↔44 for the chomp.
  const m = rad(opts.mouthDeg ?? 42);
  const t = Math.max(1.4, s * 0.05);
  if (opts.glow !== false) {
    g.fillStyle(fill, 0.1);
    g.fillCircle(0, 0, s * 0.5);
  }
  // Two stubby feet (drawn behind the body so only the rounded tips peek out below) — a creature cue.
  const footW = s * 0.18;
  const footH = s * 0.15;
  const footY = r * 0.9;
  for (const fx of [-r * 0.34, r * 0.34]) {
    g.fillStyle(fill, 1);
    g.fillRoundedRect(fx - footW / 2, footY, footW, footH, footH * 0.5);
    g.lineStyle(t, stroke, 1);
    g.strokeRoundedRect(fx - footW / 2, footY, footW, footH, footH * 0.5);
  }
  // Body: a rounded chomp wedge (circle minus the left mouth gap).
  g.fillStyle(fill, 1);
  g.slice(0, 0, r, rad(180) + m, rad(180) - m, false);
  g.fillPath();
  // Silhouette: body arc (skips the mouth) + the two jaw edges.
  g.lineStyle(t, stroke, 1);
  g.beginPath();
  g.arc(0, 0, r, rad(180) + m, rad(180) - m, false);
  g.strokePath();
  g.lineBetween(0, 0, Math.cos(rad(180) + m) * r, Math.sin(rad(180) + m) * r);
  g.lineBetween(0, 0, Math.cos(rad(180) - m) * r, Math.sin(rad(180) - m) * r);
  // Big friendly eye (upper-right, away from the mouth) — the key original-creature differentiator.
  const eyeX = r * 0.3;
  const eyeY = -r * 0.36;
  const eyeR = s * 0.13;
  g.fillStyle(0xffffff, 1);
  g.fillCircle(eyeX, eyeY, eyeR);
  g.lineStyle(Math.max(1, s * 0.022), stroke, 1);
  g.strokeCircle(eyeX, eyeY, eyeR);
  g.fillStyle(0x0a0b12, 1);
  g.fillCircle(eyeX - eyeR * 0.15, eyeY + eyeR * 0.05, eyeR * 0.5); // pupil glances toward the mouth
  g.fillStyle(0xffffff, 1);
  g.fillCircle(eyeX + eyeR * 0.12, eyeY - eyeR * 0.3, eyeR * 0.18); // catch-light
}

/** Rival muncher — a flat magenta triangle (apex right), no eye, minimal halo. */
export function drawRivalMuncher(g: Phaser.GameObjects.Graphics, s: number, opts: MuncherOpts = {}): void {
  const fill = opts.tint ?? PALETTE.ai;
  const stroke = opts.stroke ?? PALETTE.rivalStroke;
  if (opts.glow !== false) {
    g.fillStyle(fill, 0.09);
    g.fillCircle(0, 0, s * 0.46);
  }
  const ax = s * 0.46; // apex (right)
  const bx = -s * 0.34;
  const by = s * 0.4;
  g.fillStyle(fill, 1);
  g.fillTriangle(bx, -by, bx, by, ax, 0);
  const t = Math.max(1.4, s * 0.05);
  g.lineStyle(t, stroke, 1);
  g.strokeTriangle(bx, -by, bx, by, ax, 0);
  // no eye — bare geometric triangle per the redesign
}

export type MuncherKind = 'player' | 'rival';

/** The shape applyCosmeticSkin needs — matches cosmetics.ts Cosmetic without importing the model. */
export interface SkinLook {
  tint: number | null;
  hue?: number;
  sat?: number;
  /**
   * WebGL-only: fraction (0..1) to wash the sprite toward WHITE after hue/sat — a hue-preserving
   * lerp-to-white that lifts LIGHTNESS (hue/sat alone can't). Used by the ghost spectral skin to land a
   * PALE violet-white; omit (undefined) on normal cosmetics so their look is unchanged.
   */
  bright?: number;
}

/**
 * Apply a cosmetic skin to a RASTER muncher sprite. Phaser tint is MULTIPLICATIVE and the base
 * sheet is cyan (zero red channel), so warm skins (gold/crimson) can never read as their named
 * color through tint alone — "Molten Gold" came out green. On WebGL we hue-rotate with a
 * ColorMatrix postFX instead (preserves shading, the white eye and the black outline); the
 * multiply tint stays as the non-WebGL fallback. The GEOMETRIC muncher is unaffected — its fills
 * use the true tint color directly. Returns true when the FX path applied, so callers keep their
 * `baseTint` null and hit-flash tint restores never fight the FX.
 */
export function applyCosmeticSkin(spr: Phaser.GameObjects.Sprite, skin: SkinLook): boolean {
  if (skin.tint == null) return false;
  if (typeof skin.hue === 'number' && spr.scene.renderer?.type === Phaser.WEBGL) {
    try {
      const cm = spr.postFX.addColorMatrix();
      // Phaser multiply(A, true) does C := C·A, and a pixel maps out = C·p, so the LAST-multiplied
      // matrix is applied FIRST. Order here: cm.hue (reset) → saturate(true) → whiten(true) yields
      // out = HUE · SAT · WHITEN · p — WHITEN acts first, then desaturate, then hue-rotate.
      cm.hue(skin.hue);
      if (typeof skin.sat === 'number') cm.saturate(skin.sat, true);
      if (typeof skin.bright === 'number' && skin.bright > 0) {
        // Lerp each channel toward white by k: out = (1-k)·p + k. A gain (Phaser's brightness) would
        // clip R/B before G and drift a violet back toward magenta; this lifts lightness AND desaturates
        // toward white, preserving hue — the correct "pale" operation. (k in 0..1; offsets in cols 4/9/14.)
        const k = skin.bright;
        cm.multiply([1 - k, 0, 0, 0, k, 0, 1 - k, 0, 0, k, 0, 0, 1 - k, 0, k, 0, 0, 0, 1, 0], true);
      }
      return true;
    } catch {
      /* FX pipeline unsupported — multiply tint below */
    }
  }
  spr.setTint(skin.tint);
  return false;
}

/** Convenience: a positioned Graphics with the muncher drawn at its origin (the icon() twin). */
export function muncher(
  scene: Phaser.Scene,
  kind: MuncherKind,
  x: number,
  y: number,
  s: number,
  opts: MuncherOpts = {},
): Phaser.GameObjects.Graphics {
  const g = scene.add.graphics({ x, y });
  if (kind === 'rival') drawRivalMuncher(g, s, opts);
  else drawPlayerMuncher(g, s, opts);
  return g;
}

/**
 * Attach an idle bob + chomp loop to a muncher Graphics (motion-gated by the caller). Returns a
 * disposer. Re-renders the player mouth each frame via a tweened proxy, matching SplashScene.
 */
export function animateMuncher(
  scene: Phaser.Scene,
  g: Phaser.GameObjects.Graphics,
  kind: MuncherKind,
  s: number,
  opts: MuncherOpts = {},
): void {
  if (kind !== 'player') return; // rival is static (triangle has no mouth)
  const proxy = { a: opts.mouthDeg ?? 28 };
  const redraw = (): void => {
    g.clear();
    drawPlayerMuncher(g, s, { ...opts, mouthDeg: proxy.a });
  };
  scene.tweens.add({
    targets: proxy,
    a: 44,
    duration: 220,
    yoyo: true,
    repeat: -1,
    ease: 'Sine.easeInOut',
    onUpdate: redraw,
  });
}
